import React, { useEffect, useMemo, useState } from "react";
import {
  StyleSheet,
  Text,
  View,
  FlatList,
  TouchableOpacity,
  ScrollView,
  Dimensions,
  PixelRatio,
  ActivityIndicator,
  Modal,
  Platform,
} from "react-native";
import Colours from "../../utils/Colours";
import TopHeaderComponent from "../aTopHeader/TopHeaderComponent";
import { useNavigation, useRoute } from "@react-navigation/native";
import { SafeAreaView } from "react-native-safe-area-context";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Ionicons, MaterialCommunityIcons } from "@expo/vector-icons";
import CalendarPicker from "react-native-calendar-picker";
import { connectOrderSocket, disconnectOrderSocket } from "../../services/orderSocketService";
import { addOrderToQueue } from "../../services/orderQueueService";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { MERCHANT_BRAND_KEY } from "../../services/storageService";
import DoingsModeBadge from "../../icons/DoingsModeBadge";

/* ------------------ Dynamic sizing helpers ------------------ */
const { width, height } = Dimensions.get("window");
const BASE_WIDTH = 375;
const BASE_HEIGHT = 812;

const dynamicFontSize = (size) => {
  const scaleFactor = Math.min(width / BASE_WIDTH, height / BASE_HEIGHT);
  return Math.round(PixelRatio.roundToNearestPixel(size * scaleFactor));
};

const dynamicSpacing = (size) => (width / BASE_WIDTH) * size;
const dynamicSize = (size) => (width / BASE_WIDTH) * size;

const ManageOrders = () => {
  const [activeTab, setActiveTab] = useState("all_orders");
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [showCalendar, setShowCalendar] = useState(false);
  const [printingTableId, setPrintingTableId] = useState(null);

  const queryClient = useQueryClient();

  const axiosPrivate = useAxiosPrivate();
  const navigation = useNavigation();
  const route = useRoute();

  const { brandId } = route.params ?? {};

  const { data: selectedBrandData, isLoading: isBrandLoading } = useQuery({
    queryKey: ["brand", brandId],
    queryFn: async () => {
      const response = await axiosPrivate.get(`/brandById/brandById/${brandId}`); // use your real single-brand endpoint
      return response.data.success ? response.data.data : null;
    },
    enabled: !!brandId,
  });

  // console.log("selectedBrandDataXBH", selectedBrandData);

  // 1. Fetch React Query Table data
  const {
    data: allTables = [],
    isLoading,
    isFetching,
    error,
    refetch,
  } = useQuery({
    queryKey: ["tableTransactions", selectedBrandData?._id, selectedDate.toISOString()],
    queryFn: async () => {
      if (!selectedBrandData?._id) return [];

      const response = await axiosPrivate.get("/virtualTableListBrands/virtualTableListBrands", {
        params: {
          associatedBrand: selectedBrandData._id,
          date: selectedDate.toISOString(),
        },
      });

      return response.data.success ? response.data.data : [];
    },
    enabled: !!selectedBrandData?._id,
    // staleTime: 1000 * 60 * 5,
    refetchInterval: 8000, // refresh every 8 seconds
    refetchIntervalInBackground: true,
    staleTime: 5000,
  });

  //console.log("All Tables", allTables);

  // useEffect(() => {
  //   const brandID = selectedBrandData._id;
  //   console.log("brandIDbg", brandID);
  //   connectOrderSocket(brandID, handleIncomingOrder);
  // }, []);

  useEffect(() => {
    if (!selectedBrandData?._id) return;

    const saveMerchantBrand = async () => {
      try {
        const saved = await AsyncStorage.getItem(MERCHANT_BRAND_KEY);

        const currentBrand = {
          brandID: selectedBrandData._id,
          brandName: selectedBrandData.brandName,
          logoUrl: selectedBrandData.image,
        };

        if (!saved) {
          await AsyncStorage.setItem(MERCHANT_BRAND_KEY, JSON.stringify(currentBrand));

          console.log("Merchant brand saved");
          return;
        }

        const existing = JSON.parse(saved);

        // Update only if something changed
        if (
          existing.brandID !== currentBrand.brandID ||
          existing.brandName !== currentBrand.brandName ||
          existing.logoUrl !== currentBrand.logoUrl
        ) {
          await AsyncStorage.setItem(MERCHANT_BRAND_KEY, JSON.stringify(currentBrand));

          console.log(" Merchant brand updated");
        } else {
          console.log("Merchant brand already up to date");
        }
      } catch (error) {
        console.log("Failed to save merchant brand", error);
      }
    };

    saveMerchantBrand();

    connectOrderSocket(selectedBrandData._id, handleIncomingOrder);

    return () => {
      disconnectOrderSocket();
    };
  }, [selectedBrandData]);

  function handleIncomingOrder(order) {
    console.log("Saving order:", order);

    if (Platform.OS === "android") {
      addOrderToQueue(order);
    }
    //addOrderToQueue(order);

    // later printer worker picks it
  }

  // // 2. Compute dynamic operational categories + unique waiters on the floor
  // const STATUS_TABS = useMemo(() => {
  //   const baseTabs = [
  //     { key: "all_orders", label: "All Orders" },
  //     // { key: "paid_orders", label: "Paid Orders" },
  //     // { key: "unpaid_orders", label: "Unpaid Orders" },
  //   ];

  //   // Find and map distinct waiters who have an active table session assigned to them
  //   const uniqueWaitersMap = new Map();
  //   allTables.forEach((table) => {
  //     if (table.waiterID && table.waiterID._id && table.waiterID.waiterName) {
  //       uniqueWaitersMap.set(table.waiterID._id, table.waiterID.waiterName);
  //     }
  //   });

  //   const waiterTabs = Array.from(uniqueWaitersMap.entries()).map(([id, name]) => ({
  //     key: `waiter_${id}`,
  //     label: name,
  //   }));

  //   return [...baseTabs, ...waiterTabs];
  // }, [allTables]);

  // // 3. Complete Filtering Core Business Logic
  // const filteredTables = useMemo(() => {
  //   return allTables.filter((table) => {
  //     if (activeTab === "all_orders") return true;
  //     if (activeTab === "paid_orders") return table.status === "paid";
  //     if (activeTab === "unpaid_orders") return table.status === "pending_payment";

  //     if (activeTab.startsWith("waiter_")) {
  //       const targetWaiterId = activeTab.replace("waiter_", "");
  //       return table.waiterID && table.waiterID._id === targetWaiterId;
  //     }
  //     return true;
  //   });
  // }, [allTables, activeTab]);

  // 2. Compute dynamic operational categories + unique waiters on the floor
  const STATUS_TABS = useMemo(() => {
    const baseTabs = [
      { key: "all_orders", label: "All Orders" },
      { key: "paid_orders", label: "Paid Orders" },
      { key: "unpaid_orders", label: "Unpaid Orders" },
    ];

    // Find and map distinct waiters who have an active table session assigned to them
    const uniqueWaitersMap = new Map();
    allTables.forEach((table) => {
      if (table.waiterID && table.waiterID._id && table.waiterID.waiterName) {
        uniqueWaitersMap.set(table.waiterID._id, table.waiterID.waiterName);
      }
    });

    const waiterTabs = Array.from(uniqueWaitersMap.entries()).map(([id, name]) => ({
      key: `waiter_${id}`,
      label: name,
    }));

    return [...baseTabs, ...waiterTabs];
  }, [allTables]);

  // 3. Complete Filtering Core Business Logic
  const filteredTables = useMemo(() => {
    return allTables.filter((table) => {
      const orders = table.orders || [];

      if (activeTab === "all_orders") return true;

      // Table has AT LEAST ONE batch with status "paid"
      if (activeTab === "paid_orders") {
        return orders.some((order) => order.status === "paid");
      }

      // Table has AT LEAST ONE batch with status "pending"
      if (activeTab === "unpaid_orders") {
        return orders.some((order) => order.status === "pending");
      }
      // if (activeTab === "all_orders") return true;
      // if (activeTab === "paid_orders") return table.status === "paid";
      // if (activeTab === "unpaid_orders") return table.status === "pending_payment";

      if (activeTab.startsWith("waiter_")) {
        const targetWaiterId = activeTab.replace("waiter_", "");
        return table.waiterID && table.waiterID._id === targetWaiterId;
      }
      return true;
    });
  }, [allTables, activeTab]);

  const handleCheckMarking = async (item) => {
    console.log("iteeeeem", item);

    // 1. Define the exact query key you used in useQuery
    const queryKey = ["tableTransactions", selectedBrandData?._id, selectedDate.toISOString()];

    // 2. Optimistically update the cache instantly
    queryClient.setQueryData(queryKey, (oldData) => {
      if (!oldData) return [];
      return oldData.map((t) => (t._id === item._id ? { ...t, tableServed: true } : t));
    });

    try {
      // 3. Fire the network request in the background
      const response = await axiosPrivate.patch("/isTableServed/isTableServed", {
        tableId: item._id,
        tableServed: true,
      });

      if (!response.data.success) throw new Error("Server update failed");
    } catch (error) {
      console.error("Failed to mark table as served:", error);

      // 4. Rollback cache to false if the network fails completely
      queryClient.setQueryData(queryKey, (oldData) => {
        if (!oldData) return [];
        return oldData.map((t) => (t._id === item._id ? { ...t, tableServed: false } : t));
      });

      Alert.alert("Error", "Could not update the server. Please try again.");
    }
  };

  const handlePrintTable = async (table) => {
    if (printingTableId) return;
    setPrintingTableId(table._id);
    try {
      const ok = await printTableOrders(table);
      if (!ok) {
        Alert.alert(
          "Print failed",
          "Check that the printer is connected and the table has orders.",
        );
      }
    } finally {
      setPrintingTableId(null);
    }
  };

  // Empty State Placeholder Render Component
  const renderEmptyState = () => (
    <View style={styles.emptyState}>
      <MaterialCommunityIcons
        name="clipboard-text-outline"
        size={dynamicSize(48)}
        color={Colours.LIGHTGRAY}
      />
      <Text style={styles.emptyTitle}>No Orders Located</Text>
      <Text style={styles.emptySub}>
        There are no live order logs mapping under "
        {STATUS_TABS.find((t) => t.key === activeTab)?.label}" for this timeframe.
      </Text>
    </View>
  );

  if (isBrandLoading || !selectedBrandData) {
    return (
      <View style={{ flex: 1, justifyContent: "center", alignItems: "center" }}>
        <ActivityIndicator size="large" color={Colours.PRIMARY} />
      </View>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      {/* ---------------- Header ---------------- */}
      <TopHeaderComponent headerText={"Manage Orders"} hideScreen={() => navigation.goBack()} />

      <View style={styles.header}>
        <TouchableOpacity style={styles.calendarBtn} onPress={() => setShowCalendar(true)}>
          <Ionicons name="calendar-outline" size={dynamicSize(18)} color={Colours.PRIMARY} />
          <Text style={styles.calendarTxt}>{selectedDate.toDateString()}</Text>
        </TouchableOpacity>
      </View>

      {/* ---------------- Dynamic Horizontal Category Tabs ---------------- */}
      <View style={styles.tabsWrapper}>
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          style={styles.tabsContainer}
          contentContainerStyle={styles.tabsContent}
        >
          {STATUS_TABS.map((tab) => {
            const isActive = activeTab === tab.key;
            return (
              <TouchableOpacity
                key={tab.key}
                style={[styles.tabBtn, isActive && styles.tabBtnActive]}
                onPress={() => setActiveTab(tab.key)}
              >
                <Text style={[styles.tabTxt, isActive && styles.tabTxtActive]}>{tab.label}</Text>
              </TouchableOpacity>
            );
          })}
        </ScrollView>
      </View>

      <View style={styles.flatListView}>
        {isLoading ? (
          <View style={styles.centerLoading}>
            <ActivityIndicator size="large" color={Colours.PRIMARY} />
          </View>
        ) : error ? (
          <View style={styles.emptyState}>
            <MaterialCommunityIcons
              name="alert-circle-outline"
              size={dynamicSize(48)}
              color="#FF3B30"
            />
            <Text style={styles.emptyTitle}>Failed to load records</Text>
            <TouchableOpacity style={[styles.outlineBtn, { marginTop: 10 }]} onPress={refetch}>
              <Text style={styles.outlineTxt}>Retry</Text>
            </TouchableOpacity>
          </View>
        ) : (
          // <FlatList
          //   data={filteredTables}
          //   keyExtractor={(item) => item._id}
          //   ListEmptyComponent={renderEmptyState}
          //   contentContainerStyle={{ paddingBottom: dynamicSpacing(40) }}
          //   renderItem={({ item }) => {
          //     // Calculate dynamic financial metrics based on payment lifecycle state
          //     const totalOrdersCount = item.orders?.length || 0;
          //     const hasPaymentSettled = item.status === "paid";
          //     const displayingAmount = hasPaymentSettled
          //       ? (item.totalSpent ?? 0)
          //       : (item.estimatedTotalBill ?? 0);

          //     // 1. Gather all individual ordered items across all batched order entries
          //     const allOrderedItems = [];
          //     if (item.orders && item.orders.length > 0) {
          //       item.orders.forEach((batch) => {
          //         if (batch.items && batch.items.length > 0) {
          //           batch.items.forEach((menuItem) => {
          //             allOrderedItems.push({
          //               ...menuItem,
          //               orderToken: batch.orderToken, // Track which batch this item belongs to if needed
          //               batchStatus: batch.status,
          //             });
          //           });
          //         }
          //       });
          //     }

          //     return (
          //       <View style={styles.tableCard}>
          //         {/* Header Row */}
          //         <View style={styles.rowBetween}>
          //           <View>
          //             <Text style={styles.tableTitle}>{item.tableName}</Text>

          //             <Text style={styles.phoneSubTxt}>
          //               {totalOrdersCount}{" "}
          //               {totalOrdersCount === 1 ? "Batched Order" : "Batched Orders"}
          //             </Text>
          //           </View>

          //           <View
          //             style={[
          //               styles.badge,
          //               // hasPaymentSettled && styles.badgePaid,
          //               item.tableServed === false && styles.badgePending,
          //               item.tableServed === true && styles.badgePaid,
          //             ]}
          //           >
          //             <Text
          //               style={[
          //                 styles.badgeTxt,
          //                 // hasPaymentSettled && styles.badgeTxtPaid,
          //                 item.tableServed === false && styles.badgeTxtPending,
          //                 item.tableServed === true && styles.badgeTxtPaid,
          //               ]}
          //             >
          //               {/* {item.status === "pending_payment"
          //                 ? "UNPAID RUNNING"
          //                 : item.status.toUpperCase()} */}
          //               {item.tableServed ? "PAID & SERVED" : "PAID NOT SERVED"}
          //             </Text>
          //           </View>
          //         </View>

          //         {item.tableLocation ? (
          //           <View style={styles.fullWidthLocationContainer}>
          //             <MaterialCommunityIcons
          //               name="map-marker"
          //               size={dynamicSize(16)} // Slightly increased size for optical symmetry
          //               color={Colours.PRIMARY}
          //             />
          //             <View style={styles.locationTextColumn}>
          //               <Text style={styles.locationFullText}>
          //                 Location:{" "}
          //                 <Text style={styles.locationBodyHighlight}>{item.tableLocation}</Text>
          //               </Text>
          //             </View>
          //           </View>
          //         ) : (
          //           <Text style={styles.noLocationText}>No location specified</Text>
          //         )}

          //         {/* Separator Line */}
          //         <View style={styles.cardSeparator} />

          //         {/* ---- NEW COMPONENT LAYER: LINE ITEM DETAILS BREAKDOWN ---- */}
          //         {allOrderedItems.length > 0 ? (
          //           <View style={styles.itemsBreakdownContainer}>
          //             <Text style={styles.breakdownHeader}>ORDER SUMMARY</Text>
          //             {allOrderedItems.map((menuItem, index) => (
          //               <View key={`${menuItem._id || index}`} style={styles.itemRow}>
          //                 <Text style={styles.itemQuantityText}>{menuItem.quantity}x</Text>
          //                 <Text style={styles.itemNameText} numberOfLines={1}>
          //                   {menuItem.name}
          //                   {menuItem.menuType === "doings" && (
          //                     <Text style={styles.doingsTag}> [Doings]</Text>
          //                   )}
          //                 </Text>
          //                 <Text style={styles.itemPriceText}>
          //                   ₦{Number(menuItem.totalItemPrice ?? 0).toLocaleString()}
          //                 </Text>
          //               </View>
          //             ))}
          //             <View style={styles.cardSeparatorMinimal} />
          //           </View>
          //         ) : (
          //           <Text
          //             style={[
          //               styles.metaTxt,
          //               { fontStyle: "italic", marginBottom: dynamicSpacing(8) },
          //             ]}
          //           >
          //             No specific menu items recorded for this table yet.
          //           </Text>
          //         )}

          //         {/* Orders Detail Information */}
          //         <View>
          //           <Text
          //             style={[styles.metaTxt, { fontFamily: "Sen-Bold", color: Colours.DARKGRAY }]}
          //           >
          //             {hasPaymentSettled ? "Actual Amount Paid" : "Estimated Total Bill"} · ₦
          //             {displayingAmount.toLocaleString()}
          //           </Text>

          //           {item.waiterID?.waiterName && (
          //             <Text style={styles.metaTxt}>
          //               Waiter in Charge: {item.waiterID.waiterName}
          //             </Text>
          //           )}

          //           <Text style={styles.dateTxt}>
          //             Session Opened:{" "}
          //             {new Date(item.createdAt).toLocaleTimeString([], {
          //               hour: "2-digit",
          //               minute: "2-digit",
          //             })}
          //           </Text>
          //         </View>

          //         {/* Action Row Layout */}
          //         <View style={styles.actionRow}>
          //           {/* {item.status === "pending_payment" && ( */}
          //           <TouchableOpacity
          //             style={[
          //               styles.outlineBtn,
          //               item.tableServed && styles.badgePaid, // Overrides border/background when served
          //             ]}
          //             // Make sure to disable the button or add your onPress handler here
          //             onPress={() => handleCheckMarking(item)}
          //           >
          //             <Text
          //               style={[
          //                 styles.outlineTxt,
          //                 item.tableServed && styles.badgeTxtPaid, // Overrides text color when served
          //               ]}
          //             >
          //               {item.tableServed ? "Table is served" : "Mark table as served"}
          //             </Text>
          //           </TouchableOpacity>
          //           {/* )} */}

          //           {hasPaymentSettled && (
          //             <View style={styles.secondaryBtn}>
          //               <Text style={styles.secondaryTxt}>
          //                 ✓ Fulfilled by {item.waiterID?.name || "Floor Staff"}
          //               </Text>
          //             </View>
          //           )}
          //         </View>
          //       </View>
          //     );
          //   }}
          // />

          <FlatList
            data={filteredTables}
            keyExtractor={(item) => item._id}
            ListEmptyComponent={renderEmptyState}
            contentContainerStyle={{ paddingBottom: dynamicSpacing(40) }}
            renderItem={({ item }) => {
              const totalOrdersCount = item.orders?.length || 0;
              const orders = item.orders || [];

              // Check batch statuses instead of root table.status
              const hasPaymentSettled =
                orders.length > 0 &&
                orders.every((order) => order.status === "paid" || order.status === "cancelled");

              const totalPaidAmount = orders
                .filter((order) => order.status === "paid")
                .reduce((sum, order) => sum + (order.orderTotalAmount || 0), 0);

              const displayingAmount = hasPaymentSettled
                ? (item.totalSpent ?? totalPaidAmount)
                : (item.estimatedTotalBill ?? 0);

              const sortedBatches =
                item.orders && item.orders.length > 0
                  ? [...item.orders].sort(
                      (a, b) => new Date(b.orderedAt).getTime() - new Date(a.orderedAt).getTime(),
                    )
                  : [];

              // const totalOrdersCount = item.orders?.length || 0;
              // const hasPaymentSettled = item.status === "paid";
              // const displayingAmount = hasPaymentSettled
              //   ? (item.totalSpent ?? 0)
              //   : (item.estimatedTotalBill ?? 0);

              // const sortedBatches =
              //   item.orders && item.orders.length > 0
              //     ? [...item.orders].sort(
              //         (a, b) => new Date(b.orderedAt).getTime() - new Date(a.orderedAt).getTime(),
              //       )
              //     : [];

              return (
                <View style={styles.tableCard}>
                  {/* Header Row */}
                  <View style={styles.rowBetween}>
                    <View>
                      <Text style={styles.tableTitle}>{item.tableName}</Text>
                      <Text style={styles.phoneSubTxt}>
                        {totalOrdersCount}{" "}
                        {totalOrdersCount === 1 ? "Batched Order" : "Batched Orders"}
                      </Text>
                    </View>
                    {/* 
                    <View
                      style={[
                        styles.badge,
                        item.tableServed === false && styles.badgePending,
                        item.tableServed === true && styles.badgePaid,
                      ]}
                    >
                      <Text
                        style={[
                          styles.badgeTxt,
                          item.tableServed === false && styles.badgeTxtPending,
                          item.tableServed === true && styles.badgeTxtPaid,
                        ]}
                      >
                        {item.tableServed ? "PAID & SERVED" : "PAID NOT SERVED"}
                      </Text>
                    </View> */}
                    <View
                      style={[
                        styles.badge,
                        hasPaymentSettled ? styles.badgePaid : styles.badgePending,
                      ]}
                    >
                      <Text
                        style={[
                          styles.badgeTxt,
                          hasPaymentSettled ? styles.badgeTxtPaid : styles.badgeTxtPending,
                        ]}
                      >
                        {hasPaymentSettled ? "PAID" : "PENDING"}
                      </Text>
                    </View>
                  </View>

                  {item.tableLocation ? (
                    <View style={styles.fullWidthLocationContainer}>
                      <MaterialCommunityIcons
                        name="map-marker"
                        size={dynamicSize(16)}
                        color={Colours.PRIMARY}
                      />
                      <View style={styles.locationTextColumn}>
                        <Text style={styles.locationFullText}>
                          Location:{" "}
                          <Text style={styles.locationBodyHighlight}>{item.tableLocation}</Text>
                        </Text>
                      </View>
                    </View>
                  ) : (
                    <Text style={styles.noLocationText}>No location specified</Text>
                  )}

                  {/* Table Number (Bold, Bottom-Right after Location) */}
                  <View style={styles.tableNumberContainer}>
                    <Text style={styles.tableNumberText}>
                      Table - {String(item.tableNumber ?? "").padStart(3, "0")}
                    </Text>
                  </View>

                  {/* Separator Line */}
                  <View style={styles.cardSeparator} />

                  {/* ---- NEW COMPONENT LAYER: BATCHED ORDERS INFRASTRUCTURE ---- */}
                  {sortedBatches.length > 0 ? (
                    <View style={styles.batchesMasterContainer}>
                      <Text style={styles.breakdownHeader}>ORDER BATCHES</Text>

                      {sortedBatches.map((batch, batchIndex) => {
                        const isLatestBatch = batchIndex === 0 && !item.tableServed;
                        return (
                          <View
                            key={batch.orderToken || batch._id || batchIndex}
                            style={[
                              styles.batchWrapper,
                              isLatestBatch ? styles.latestBatchBg : styles.olderBatchBg,
                            ]}
                          >
                            {/* Batch Sub-Header Metas */}
                            <View style={styles.batchHeaderRow}>
                              <View style={styles.batchTitleContainer}>
                                <Text
                                  style={[
                                    styles.batchTokenTxt,
                                    isLatestBatch && styles.latestBatchTextHighlight,
                                  ]}
                                >
                                  {isLatestBatch
                                    ? "🔥 LATEST ORDER BATCH"
                                    : `BATCH #${sortedBatches.length - batchIndex}`}
                                </Text>
                                {(batch.status === "paid" || hasPaymentSettled) && (
                                  <View style={{ marginLeft: 6 }}>
                                    <DoingsModeBadge size={dynamicSize(15)} color="#2ECC71" />
                                  </View>
                                )}
                              </View>

                              <Text style={styles.batchTimeTxt}>
                                {new Date(batch.orderedAt).toLocaleTimeString([], {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                })}
                              </Text>
                            </View>

                            {/* Batch Items Loop */}
                            {batch.items?.map((menuItem, itemIndex) => (
                              <View key={`${menuItem._id || itemIndex}`} style={styles.itemRow}>
                                <Text style={styles.itemQuantityText}>{menuItem.quantity}x</Text>
                                <Text style={styles.itemNameText} numberOfLines={1}>
                                  {menuItem.name}
                                  {menuItem.menuType === "doings" && (
                                    <Text style={styles.doingsTag}> [Doings]</Text>
                                  )}
                                </Text>
                                <Text style={styles.itemPriceText}>
                                  ₦{Number(menuItem.totalItemPrice ?? 0).toLocaleString()}
                                </Text>
                              </View>
                            ))}

                            {/* Batch Financial Subtotal Summary */}
                            <View style={styles.batchSubTotalRow}>
                              <Text style={styles.batchSubTotalLabel}>Batch Total:</Text>
                              <Text style={styles.batchSubTotalVal}>
                                ₦{Number(batch.orderTotalAmount ?? 0).toLocaleString()}
                              </Text>
                            </View>
                          </View>
                        );
                      })}
                    </View>
                  ) : (
                    <Text
                      style={[
                        styles.metaTxt,
                        { fontStyle: "italic", marginBottom: dynamicSpacing(8) },
                      ]}
                    >
                      No specific menu items recorded for this table yet.
                    </Text>
                  )}

                  {/* Orders Detail Information */}
                  <View>
                    <Text
                      style={[styles.metaTxt, { fontFamily: "Sen-Bold", color: Colours.DARKGRAY }]}
                    >
                      {hasPaymentSettled ? "Actual Amount Paid" : "Estimated Total Bill"} · ₦
                      {displayingAmount.toLocaleString()}
                    </Text>

                    {item.waiterID?.waiterName && (
                      <Text style={styles.metaTxt}>
                        Waiter in Charge: {item.waiterID.waiterName}
                      </Text>
                    )}

                    <Text style={styles.dateTxt}>
                      Session Opened:{" "}
                      {new Date(item.createdAt).toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </Text>
                  </View>

                  {/* Action Row Layout */}
                  {/* <View style={styles.actionRow}>
                    <TouchableOpacity
                      style={[styles.outlineBtn, item.tableServed && styles.badgePaid]}
                      disabled={item.tableServed}
                      onPress={() => handleCheckMarking(item)}
                    >
                      <Text style={[styles.outlineTxt, item.tableServed && styles.badgeTxtPaid]}>
                        {item.tableServed ? "Table is served" : "Mark table as served"}
                      </Text>
                    </TouchableOpacity>

                    
                  </View> */}
                  <View style={styles.actionRow}>
                    <TouchableOpacity
                      style={[
                        styles.outlineBtn,
                        styles.actionBtn,
                        item.tableServed && styles.badgePaid,
                      ]}
                      disabled={item.tableServed}
                      onPress={() => handleCheckMarking(item)}
                    >
                      <Text style={[styles.outlineTxt, item.tableServed && styles.badgeTxtPaid]}>
                        {item.tableServed ? "Table is served" : "Mark table as served"}
                      </Text>
                    </TouchableOpacity>

                    <TouchableOpacity
                      style={[
                        styles.printBtn,
                        styles.actionBtn,
                        printingTableId === item._id && { opacity: 0.6 },
                      ]}
                      disabled={printingTableId === item._id || totalOrdersCount === 0}
                      onPress={() => handlePrintTable(item)}
                    >
                      <MaterialCommunityIcons name="printer" size={dynamicSize(16)} color="#fff" />
                      <Text style={styles.printTxt}>
                        {printingTableId === item._id ? "Printing..." : "Print"}
                      </Text>
                    </TouchableOpacity>
                  </View>
                </View>
              );
            }}
          />
        )}
      </View>

      <Modal visible={showCalendar} animationType="slide" transparent={true}>
        <View style={styles.calendarModal}>
          <View style={styles.calendarContainer}>
            <CalendarPicker
              width={320}
              maxDate={new Date()}
              onDateChange={(date) => {
                setSelectedDate(date);
                setShowCalendar(false);
              }}
              selectedStartDate={selectedDate}
              selectedDayColor={Colours.PRIMARY}
              selectedDayTextColor="#FFFFFF"
            />
            <TouchableOpacity style={styles.closeBtn} onPress={() => setShowCalendar(false)}>
              <Text style={{ color: "white" }}>Close</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>
    </SafeAreaView>
  );
};

export default ManageOrders;

/* ------------------ Styles ------------------ */
const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Colours.LIGHTEST,
  },
  flatListView: {
    flex: 1,
  },
  batchTitleContainer: {
    flexDirection: "row",
    alignItems: "center",
  },
  centerLoading: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    marginTop: dynamicSpacing(100),
  },
  header: {
    padding: dynamicSpacing(15),
    backgroundColor: "white",
    borderBottomWidth: 1,
    borderBottomColor: Colours.LIGHTGRAY,
  },
  calendarBtn: {
    flexDirection: "row",
    alignItems: "center",
    gap: dynamicSpacing(8),
    backgroundColor: Colours.PRIMARY_LIGHTER,
    padding: dynamicSpacing(10),
    borderRadius: dynamicSize(10),
    alignSelf: "flex-start",
  },
  calendarTxt: {
    fontSize: dynamicFontSize(14),
    color: Colours.PRIMARY,
    fontFamily: "Sen-Regular",
  },
  tabsWrapper: {
    backgroundColor: "white",
    paddingVertical: dynamicSpacing(6),
    borderBottomWidth: 1,
    borderBottomColor: Colours.LIGHTGRAY,
  },
  tabTxt: {
    fontSize: dynamicFontSize(13),
    color: Colours.DARKGRAY,
    fontFamily: "Sen-Regular",
  },
  tabTxtActive: {
    color: "white",
    fontFamily: "Sen-Bold",
  },
  tableCard: {
    marginHorizontal: dynamicSpacing(15),
    marginTop: dynamicSpacing(15),
    padding: dynamicSpacing(15),
    borderRadius: dynamicSize(15),
    backgroundColor: "white",
    elevation: 2,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 2,
  },

  tabBtn: {
    paddingVertical: dynamicSpacing(8),
    paddingHorizontal: dynamicSpacing(15),
    marginRight: dynamicSpacing(8),
    borderRadius: dynamicSize(20),
    backgroundColor: Colours.LIGHTEST,
    borderWidth: 1,
    borderColor: Colours.LIGHTGRAY,
  },

  tabBtnActive: {
    backgroundColor: Colours.PRIMARY,
    borderColor: Colours.PRIMARY,
  },
  tabsContainer: {
    display: "flex",
    flexGrow: 0,

    paddingVertical: dynamicSpacing(10),
    paddingLeft: dynamicSpacing(15),
  },

  tabsContent: {
    display: "flex",
    height: dynamicSize(60),
    paddingRight: dynamicSpacing(30),
  },

  // Grouping containers for separated batch UI blocks
  batchesMasterContainer: {
    marginBottom: dynamicSpacing(12),
  },
  batchWrapper: {
    padding: dynamicSpacing(10),
    borderRadius: dynamicSize(8),
    marginBottom: dynamicSpacing(8),
    borderWidth: 1,
  },

  // 🔥 Dynamic background structural shifts
  latestBatchBg: {
    backgroundColor: "#FFF9E6", // Subtle yellow/gold tint for high priority focus
    borderColor: "#FFE082",
  },
  olderBatchBg: {
    backgroundColor: "#FAFAFA", // Muted standard color variant
    borderColor: "#EFEFEF",
  },

  batchHeaderRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    borderBottomWidth: 1,
    borderBottomColor: "rgba(0,0,0,0.05)",
    paddingBottom: dynamicSpacing(4),
    marginBottom: dynamicSpacing(6),
  },
  batchTokenTxt: {
    fontSize: dynamicFontSize(11),
    fontFamily: "Sen-Bold",
    color: "#757575",
    letterSpacing: 0.5,
  },
  latestBatchTextHighlight: {
    color: "#E65100", // Strong accent color context for the live batch indicator
  },
  batchTimeTxt: {
    fontSize: dynamicFontSize(10),
    fontFamily: "Sen-Regular",
    color: "#9E9E9E",
  },
  batchSubTotalRow: {
    flexDirection: "row",
    justifyContent: "flex-end",
    alignItems: "center",
    marginTop: dynamicSpacing(6),
    paddingTop: dynamicSpacing(4),
    borderTopWidth: 1,
    borderTopColor: "rgba(0,0,0,0.03)",
  },
  batchSubTotalLabel: {
    fontSize: dynamicFontSize(11),
    fontFamily: "Sen-Regular",
    color: Colours.DARKLIGHTGRAY,
    marginRight: dynamicSpacing(4),
  },
  batchSubTotalVal: {
    fontSize: dynamicFontSize(12),
    fontFamily: "Sen-Bold",
    color: Colours.DARKGRAY,
  },

  // Table Number positioning right below Location (Bottom-Right aligned)
  tableNumberContainer: {
    alignSelf: "flex-end",
    marginTop: dynamicSpacing(8),
  },
  tableNumberText: {
    fontSize: dynamicFontSize(13),
    fontFamily: "Sen-Bold",
    color: Colours.PRIMARY,
    letterSpacing: 0.5,
  },

  actionRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    gap: dynamicSpacing(10),
    marginTop: dynamicSpacing(12),
  },
  actionBtn: {
    flex: 1,
  },
  printBtn: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    backgroundColor: Colours.PRIMARY,
    borderRadius: 8,
    paddingVertical: dynamicSpacing(10),
  },
  printTxt: {
    color: "#fff",
    fontFamily: "Sen-Bold",
  },

  // NEW STYLE ASSIGNMENTS FOR TABLE LOCATION ROUTING INFRASTRUCTURE
  fullWidthLocationContainer: {
    flexDirection: "row",
    alignItems: "center", // 🎯 Centers the pin icon and text along a straight line
    justifyContent: "center", // Enforces central horizontal weight balancing
    backgroundColor: Colours.PRIMARY_LIGHTER,
    paddingHorizontal: dynamicSpacing(12),
    paddingVertical: dynamicSpacing(8),
    borderRadius: dynamicSize(8),
    marginTop: dynamicSpacing(10),
    gap: dynamicSpacing(6),
    width: "100%",
  },
  locationTextColumn: {
    flex: 1,
    justifyContent: "center", // Double-down centering inside the layout block wrapper
  },
  locationFullText: {
    fontSize: dynamicFontSize(12),
    fontFamily: "Sen-Regular",
    color: Colours.DARKGRAY,
    includeFontPadding: false, // 🛡️ Removes internal Android engine baseline padding drift
    textAlignVertical: "center", // Cross-platform font centering support
  },
  locationBodyHighlight: {
    fontFamily: "Sen-Bold",
    color: Colours.PRIMARY,
  },
  noLocationText: {
    fontSize: dynamicFontSize(11),
    fontFamily: "Sen-Regular",
    color: "#B0B0B0",
    fontStyle: "italic",
    marginTop: dynamicSpacing(8),
  },

  rowBetween: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  tableTitle: {
    fontSize: dynamicFontSize(16),
    fontFamily: "Sen-Bold",
    color: Colours.DARKGRAY,
  },
  metaTxt: {
    fontSize: dynamicFontSize(14),
    color: Colours.DARKLIGHTGRAY,
    fontFamily: "Sen-Regular",
    marginTop: dynamicSpacing(5),
  },
  badge: {
    paddingVertical: dynamicSpacing(4),
    paddingHorizontal: dynamicSpacing(10),
    borderRadius: dynamicSize(10),
  },
  badgeTxt: {
    fontSize: dynamicFontSize(11),
    fontFamily: "Sen-Bold",
  },
  // actionRow: {
  //   marginTop: dynamicSpacing(12),
  //   flexDirection: "row",
  // },
  secondaryBtn: {
    backgroundColor: Colours.PRIMARY_LIGHTER,
    paddingVertical: dynamicSpacing(8),
    paddingHorizontal: dynamicSpacing(14),
    borderRadius: dynamicSize(10),
  },
  secondaryTxt: {
    color: Colours.PRIMARY,
    fontFamily: "Sen-Bold",
    fontSize: dynamicFontSize(13),
  },
  outlineBtn: {
    borderWidth: 1,
    borderColor: Colours.PRIMARY,
    paddingVertical: dynamicSpacing(8),
    paddingHorizontal: dynamicSpacing(14),
    borderRadius: dynamicSize(10),
  },
  outlineTxt: {
    color: Colours.PRIMARY,
    fontFamily: "Sen-Regular",
    fontSize: dynamicFontSize(13),
  },
  emptyState: {
    alignItems: "center",
    marginTop: dynamicSpacing(80),
    paddingHorizontal: dynamicSpacing(40),
  },
  emptyTitle: {
    fontSize: dynamicFontSize(18),
    fontFamily: "Sen-Bold",
    color: Colours.DARKGRAY,
    marginTop: dynamicSpacing(15),
  },
  emptySub: {
    fontSize: dynamicFontSize(14),
    fontFamily: "Sen-Regular",
    color: Colours.DARKLIGHTGRAY,
    textAlign: "center",
    marginTop: dynamicSpacing(5),
    lineHeight: dynamicFontSize(20),
  },
  phoneSubTxt: {
    fontSize: dynamicFontSize(12),
    color: Colours.DARKLIGHTGRAY,
    fontFamily: "Sen-Regular",
    marginTop: dynamicSpacing(2),
  },
  dateTxt: {
    fontSize: dynamicFontSize(11),
    color: "#9E9E9E",
    fontFamily: "Sen-Regular",
    marginTop: dynamicSpacing(8),
  },
  cardSeparator: {
    height: 1,
    backgroundColor: "#F5F5F5",
    marginVertical: dynamicSpacing(12),
  },
  badgePaid: {
    backgroundColor: "#E6F4EA",
    borderColor: "#137333",
  },
  badgePending: {
    backgroundColor: "#FFF4E5",
  },
  badgeClosed: {
    backgroundColor: "#F1F3F4",
  },
  badgeTxtPaid: {
    color: "#137333",
  },
  badgeTxtPending: {
    color: "#B06000",
  },
  badgeTxtClosed: {
    color: "#5F6368",
  },
  calendarModal: {
    flex: 1,
    justifyContent: "center",
    backgroundColor: "rgba(0,0,0,0.4)",
  },
  calendarContainer: {
    margin: dynamicSpacing(20),
    backgroundColor: "white",
    borderRadius: dynamicSize(15),
    padding: dynamicSize(15),
    alignItems: "center",
  },
  closeBtn: {
    marginTop: dynamicSpacing(15),
    backgroundColor: Colours.PRIMARY,
    paddingVertical: dynamicSpacing(10),
    paddingHorizontal: dynamicSpacing(30),
    borderRadius: dynamicSize(8),
    alignItems: "center",
  },

  itemsBreakdownContainer: {
    backgroundColor: "#FAFAFA",
    padding: dynamicSpacing(10),
    borderRadius: dynamicSize(8),
    marginBottom: dynamicSpacing(12),
    borderWidth: 1,
    borderColor: "#F0F0F0",
  },
  breakdownHeader: {
    fontSize: dynamicFontSize(11),
    fontFamily: "Sen-Bold",
    color: "#9E9E9E",
    letterSpacing: 1,
    marginBottom: dynamicSpacing(6),
  },
  itemRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingVertical: dynamicSpacing(4),
  },
  itemQuantityText: {
    fontSize: dynamicFontSize(13),
    fontFamily: "Sen-Bold",
    color: Colours.PRIMARY,
    width: dynamicSize(30),
  },
  itemNameText: {
    flex: 1,
    fontSize: dynamicFontSize(13),
    fontFamily: "Sen-Regular",
    color: Colours.DARKGRAY,
    paddingRight: dynamicSpacing(10),
  },
  doingsTag: {
    fontSize: dynamicFontSize(10),
    color: "#B06000",
    fontFamily: "Sen-Bold",
  },
  itemPriceText: {
    fontSize: dynamicFontSize(13),
    fontFamily: "Sen-Regular",
    color: Colours.DARKLIGHTGRAY,
  },
  cardSeparatorMinimal: {
    height: 1,
    backgroundColor: "#EFEFEF",
    marginTop: dynamicSpacing(8),
    marginBottom: dynamicSpacing(2),
  },
});
